import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { FormProvider } from "react-hook-form";
import { useTranslationApp } from "../../../../lib/i18next";
import Button from "../../../../components/Buttons/Button";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { GOOGLE, LINKEDIN, META, TIKTOK } from "../../../../utils/constants";
import {
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_LOADING,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_SAVE,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_TITLE,
} from "../../../../i18n/keysTranslations";
import { usePlatformDefaultsSettingsForm } from "../../../../hooks/platformDefaults/usePlatformDefaultsSettingsForm";
import PlatformDefaultsFormMeta from "./forms/PlatformDefaultsFormMeta";
import PlatformDefaultsFormTikTok from "./forms/PlatformDefaultsFormTikTok";
import PlatformDefaultsFormGoogle from "./forms/PlatformDefaultsFormGoogle";
import PlatformDefaultsFormLinkedin from "./forms/PlatformDefaultsFormLinkedin";

const SAVE_BUTTON_SX = {
  height: 38,
  minHeight: 38,
  minWidth: 120,
  py: "6px",
  textTransform: "none",
  flexShrink: 0,
  width: { xs: "100%", sm: "auto" },
};

const LOADING_STATE_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

const LOADING_SKELETON_COUNT = 3;
const LOADING_SKELETON_HEIGHT = 56;

const PlatformDefaultsLoadingState = () => {
  const { t } = useTranslationApp();

  return (
    <Box sx={LOADING_STATE_SX}>
      <Typography variant="body2" color="text.secondary">
        {t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_LOADING)}
      </Typography>
      {Array.from({ length: LOADING_SKELETON_COUNT }, (_, index) => (
        <Skeleton
          key={index}
          variant="rounded"
          height={LOADING_SKELETON_HEIGHT}
        />
      ))}
    </Box>
  );
};

const PlatformDefaultsFormContent = ({ FormComponent, formMethods }) => (
  <FormProvider {...formMethods}>
    <FormComponent formMethods={formMethods} />
  </FormProvider>
);

const FORM_BY_PLATFORM = {
  [META]: PlatformDefaultsFormMeta,
  [TIKTOK]: PlatformDefaultsFormTikTok,
  [GOOGLE]: PlatformDefaultsFormGoogle,
  [LINKEDIN]: PlatformDefaultsFormLinkedin,
};

const PlatformDefaultsSettingsSection = ({ platform }) => {
  const { t } = useTranslationApp();
  const { formMethods, isLoadingDefaults, isSaving, saveError, onSave } =
    usePlatformDefaultsSettingsForm({ platform });

  const FormComponent = FORM_BY_PLATFORM[platform];

  if (!FormComponent) {
    return null;
  }

  return (
    <Box
      sx={{
        borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
        px: 3,
        py: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_TITLE)}
        </Typography>
      </Box>

      {isLoadingDefaults ? (
        <PlatformDefaultsLoadingState />
      ) : (
        <PlatformDefaultsFormContent
          FormComponent={FormComponent}
          formMethods={formMethods}
        />
      )}

      {saveError ? (
        <Typography variant="body2" color="error">
          {saveError}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "stretch", sm: "flex-end" },
        }}
      >
        <Button
          id={`connected-accounts-platform-defaults-save-${platform}`}
          onClick={() => {
            void onSave();
          }}
          loading={isSaving}
          disabled={isLoadingDefaults}
          sx={SAVE_BUTTON_SX}
        >
          {t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_SAVE)}
        </Button>
      </Box>
    </Box>
  );
};

export default PlatformDefaultsSettingsSection;
